import { useMemo, useState } from 'react';
import EmptyState from '../components/EmptyState';
import CourseDetailView from '../components/CourseDetailView';
import courseCatalog from '../data/course-catalog.json';
import courseStatus from '../data/course-status.json';
import coursePublished from '../data/course-published.json';
import { TARGET_LANGUAGES, COUNTRY_NAMES } from '../data/target-languages';

interface CatalogEntry {
  industry: string;
  courses: string[];
}

type StatusColor = 'green' | 'yellow' | 'red';

// courseStatus is keyed [source][target][country][courseKey] -> 'green' | 'yellow'.
// Anything absent is treated as 'red' (undrafted). See audio-course/config/course-status.json
// (canonical, hand-edited) — admin/web/src/data/course-status.json is the bundled copy this
// page imports, kept in sync the same way course-catalog.json is derived from the TSV.
interface StatusRegistry {
  _note?: string;
  [source: string]: Record<string, Record<string, Record<string, StatusColor>>> | string | undefined;
}

// courseStatus is keyed [source][target][country][courseKey] -> boolean. Flipped by Blitzscale
// once a course has shipped to the store; all false today. Same key scheme as STATUS above.
interface PublishedRegistry {
  [source: string]: Record<string, Record<string, Record<string, boolean>>> | undefined;
}

const CATALOG = courseCatalog as CatalogEntry[];
const STATUS: StatusRegistry = courseStatus as StatusRegistry;
const PUBLISHED: PublishedRegistry = coursePublished as PublishedRegistry;

// Courses we currently have full preview content for (course-previews/<key>.json), discovered
// at build time via Vite's glob import. Only these are clickable in the matrix — everything
// else is still undrafted (or drafted without a preview file yet).
const PREVIEW_KEYS = new Set(
  Object.keys(import.meta.glob('../data/course-previews/*.json')).map((p) => {
    const m = /([^/]+)\.json$/.exec(p);
    return m?.[1] ?? p;
  }),
);

// Source options. Only English for now.
const SOURCE_OPTIONS = [{ code: 'en', label: 'English' }];

// Country options for a given target: its native/home market plus US, GB, AU (deduplicated).
// US is always included, so it's always a safe default.
function countryOptionsFor(targetCode: string): string[] {
  const native = TARGET_LANGUAGES.find((t) => t.code === targetCode)?.native ?? 'US';
  return Array.from(new Set([native, 'US', 'GB', 'AU']));
}

type StatusFilter = StatusColor | 'published' | 'all';

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'green', label: 'Green' },
  { key: 'yellow', label: 'Yellow' },
  { key: 'red', label: 'Red' },
  { key: 'published', label: 'Published' },
  { key: 'all', label: 'All' },
];

function filterChipClass(active: boolean, variant: StatusFilter): string {
  const base = 'px-3 py-1 rounded-full text-xs font-medium border transition-colors';
  if (!active) {
    return `${base} border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200`;
  }
  const activeColors: Record<StatusFilter, string> = {
    all: 'border-teal-500 text-teal-300 bg-teal-500/10',
    green: 'border-emerald-400 text-emerald-300 bg-emerald-400/10',
    yellow: 'border-amber-500 text-amber-300 bg-amber-500/10',
    red: 'border-rose-500 text-rose-300 bg-rose-500/10',
    published: 'border-emerald-500 text-emerald-300 bg-emerald-500/10',
  };
  return `${base} ${activeColors[variant]}`;
}

// Normalization applied to catalog Subsection strings to get a course key, matching the
// scheme documented in audio-course/config/course-status.json's _note. Keep this in sync
// with that note if the scheme ever changes.
function courseKey(subsection: string): string {
  return subsection
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function resolveStatus(source: string, target: string, country: string, course: string): StatusColor {
  const sourceMap = STATUS[source];
  if (!sourceMap || typeof sourceMap === 'string') return 'red';
  const color = sourceMap[target]?.[country]?.[courseKey(course)];
  return color ?? 'red';
}

function resolvePublished(source: string, target: string, country: string, course: string): boolean {
  const sourceMap = PUBLISHED[source];
  if (!sourceMap) return false;
  return sourceMap[target]?.[country]?.[courseKey(course)] ?? false;
}

function statusDot(status: StatusColor, published: boolean) {
  const cls =
    status === 'green' ? 'bg-emerald-400' : status === 'yellow' ? 'bg-amber-500/70' : 'bg-rose-500/60';
  const title = status === 'green' ? 'Transcripts done' : status === 'yellow' ? 'Draft only' : 'Undrafted';
  return (
    <span className="relative inline-block w-3 h-3 shrink-0">
      <span className={`block w-3 h-3 rounded-full ${cls}`} title={title} />
      {published && (
        <span
          className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-2.5 h-2.5 rounded-full bg-emerald-600 ring-1 ring-zinc-900 text-[7px] leading-none text-white"
          title="Published to store"
        >
          ✓
        </span>
      )}
    </span>
  );
}

export default function CourseMatrix() {
  const [source, setSource] = useState('en');
  const [target, setTarget] = useState('spa');
  const [country, setCountry] = useState('US');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [detailCourse, setDetailCourse] = useState<{ key: string; title: string } | null>(null);

  const countryOptions = useMemo(() => countryOptionsFor(target), [target]);
  // Registries are keyed [source][composedTarget][country][courseKey], where composedTarget
  // is `${targetBase}_${country}` (e.g. spa + US -> "spa_us"). The 5 built courses live at
  // [en][spa_us][US].
  const composedTarget = `${target}_${country.toLowerCase()}`;

  const handleTargetChange = (newTarget: string) => {
    setTarget(newTarget);
    setCountry('US');
  };

  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    return CATALOG
      .map((entry) => {
        let courses = entry.courses;
        if (q && !entry.industry.toLowerCase().includes(q)) {
          courses = courses.filter((c) => c.toLowerCase().includes(q));
        }
        if (statusFilter !== 'all') {
          courses = courses.filter((c) =>
            statusFilter === 'published'
              ? resolvePublished(source, composedTarget, country, c)
              : resolveStatus(source, composedTarget, country, c) === statusFilter,
          );
        }
        return courses.length > 0 ? { ...entry, courses } : null;
      })
      .filter((e): e is CatalogEntry => e !== null);
  }, [search, statusFilter, source, composedTarget, country]);

  const totals = useMemo(() => {
    let courses = 0;
    let green = 0;
    let yellow = 0;
    let red = 0;
    for (const entry of CATALOG) {
      for (const course of entry.courses) {
        courses++;
        const status = resolveStatus(source, composedTarget, country, course);
        if (status === 'green') green++;
        else if (status === 'yellow') yellow++;
        else red++;
      }
    }
    return { industries: CATALOG.length, courses, green, yellow, red };
  }, [source, composedTarget, country]);

  const toggleIndustry = (industry: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(industry)) next.delete(industry);
      else next.add(industry);
      return next;
    });
  };

  const toggleAll = () => {
    if (allExpanded) {
      setExpanded(new Set());
      setAllExpanded(false);
    } else {
      setExpanded(new Set(filteredCatalog.map((e) => e.industry)));
      setAllExpanded(true);
    }
  };

  if (CATALOG.length === 0) {
    return (
      <EmptyState
        title="Course Matrix"
        description="No industry course catalog found. Run audio-course/tools/build_course_catalog.py to generate admin/web/src/data/course-catalog.json."
        phase={null}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-zinc-100">Course Matrix</h1>
          <p className="text-zinc-400 text-sm mt-1">
            {totals.industries} industries &middot; {totals.courses} courses &middot; {totals.green} with transcripts
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="px-2 py-1.5 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-xs font-mono w-full sm:w-auto focus:outline-none focus:border-teal-500"
        >
          {SOURCE_OPTIONS.map((s) => (
            <option key={s.code} value={s.code}>
              {s.code} — {s.label}
            </option>
          ))}
        </select>

        <select
          value={target}
          onChange={(e) => handleTargetChange(e.target.value)}
          className="px-2 py-1.5 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-xs font-mono w-full sm:w-auto focus:outline-none focus:border-teal-500"
        >
          {TARGET_LANGUAGES.map((t) => (
            <option key={t.code} value={t.code}>
              {t.code} — {t.name}
            </option>
          ))}
        </select>

        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="px-2 py-1.5 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-xs font-mono w-full sm:w-auto focus:outline-none focus:border-teal-500"
        >
          {countryOptions.map((c) => (
            <option key={c} value={c}>
              {c} — {COUNTRY_NAMES[c] ?? c}
            </option>
          ))}
        </select>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter industries / courses..."
          className="px-2 py-1.5 rounded bg-zinc-700 border border-zinc-600 text-zinc-200 text-xs w-full sm:w-64 focus:outline-none focus:border-teal-500"
        />

        <button
          onClick={toggleAll}
          className="px-3 py-1.5 rounded text-xs font-medium bg-zinc-700 text-zinc-200 hover:bg-zinc-600"
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={filterChipClass(statusFilter === f.key, f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400" /> Transcripts done
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500/70" /> Draft only
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-500/60" /> Undrafted
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-2.5 h-2.5 rounded-full bg-emerald-600 text-[7px] leading-none text-white">
              ✓
            </span>{' '}
          = published to store
        </span>
      </div>

      {filteredCatalog.length === 0 ? (
        <EmptyState title="No matches" description="No industries or courses match this filter." phase={null} />
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[640px] space-y-2">
            {filteredCatalog.map((entry) => {
              const isOpen = expanded.has(entry.industry);
              return (
                <div key={entry.industry} className="bg-zinc-800 border border-zinc-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleIndustry(entry.industry)}
                    className="w-full flex items-center justify-between gap-3 px-3 md:px-4 py-2.5 text-left hover:bg-zinc-800/70"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-zinc-500 text-xs shrink-0">{isOpen ? '−' : '+'}</span>
                      <span className="text-zinc-100 font-medium truncate">{entry.industry}</span>
                    </span>
                    <span className="text-xs text-zinc-500 font-mono shrink-0">{entry.courses.length} courses</span>
                  </button>

                  {isOpen && (
                    <div className="border-t border-zinc-700">
                      <table className="w-full text-sm border-collapse">
                        <tbody>
                          {entry.courses.map((course) => {
                            const status = resolveStatus(source, composedTarget, country, course);
                            const published = resolvePublished(source, composedTarget, country, course);
                            const key = courseKey(course);
                            const clickable = PREVIEW_KEYS.has(key);
                            return (
                              <tr
                                key={course}
                                onClick={clickable ? () => setDetailCourse({ key, title: course }) : undefined}
                                className={`border-b border-zinc-800/50 last:border-b-0 ${
                                  clickable ? 'cursor-pointer hover:bg-zinc-800/50' : 'cursor-default'
                                }`}
                                title={clickable ? 'View course contents + cover' : 'Not drafted yet — no preview available'}
                              >
                                <td className={`px-3 md:px-4 py-1.5 ${clickable ? 'text-zinc-300' : 'text-zinc-500'}`}>
                                  {course}
                                </td>
                                <td className="px-3 md:px-4 py-1.5 w-10 text-right">
                                  <div className="flex items-center justify-end gap-1.5">{statusDot(status, published)}</div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {detailCourse && (
        <CourseDetailView
          courseKey={detailCourse.key}
          catalogTitle={detailCourse.title}
          onClose={() => setDetailCourse(null)}
        />
      )}
    </div>
  );
}
